import { useEffect, useRef, useState } from 'react';
import '../Dashboard.scss';
import * as echarts from 'echarts';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Button from '@mui/material/Button';
import { COMPACT_FONT_SIZE, downloadCSV, downloadIcon } from './utils';

const ChartComponent = ({
  results,
  compact = false,
  ci,
}: {
  results: any;
  compact?: boolean;
  ci?: boolean;
}) => {
  const chartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartType, setChartType] = useState('pie');

  useEffect(() => {
    const chart = echarts.init(chartRef.current);

    const pieData = results.map((e: { [x: string]: any }) => ({
      value: e['PercentageOfTotal'],
      name: `${e['Original Equipment Type'] || e['Updated Equipment Type'] || e['Facility Type']} (${e['EquipmentCount']})`,
    }));

    const stackedData = results.slice(0, 2).map((e: any) => ({
      name:
        e['Original Equipment Type'] ||
        e['Updated Equipment Type'] ||
        e['Facility Type'],
      type: 'bar',
      stack: 'stackGroup',
      data: [e['TotalEmissionRate'], 0].map((e) => e.toFixed(1)),
    }));

    const normalData = results.slice(2).map((e: any) => ({
      name:
        e['Original Equipment Type'] ||
        e['Updated Equipment Type'] ||
        e['Facility Type'],
      type: 'bar',
      stack: null,
      data: [0, e['TotalEmissionRate']].map((e) => e.toFixed(1)),
    }));

    const optionPie = {
      title: {
        text: 'Total Emissions',
        left: 'center',
        ...(compact
          ? {
              textStyle: {
                fontSize: COMPACT_FONT_SIZE.toString() + 'px',
              },
            }
          : {}),
      },
      grid: {
        top: compact ? 30 : 60,
        bottom: compact ? 20 : 40,
        left: compact ? 40 : 50,
        right: compact ? 20 : 30,
      },
      tooltip: {
        trigger: 'item',
        formatter: function (params: {
          seriesName: any;
          name: any;
          value: any;
          percent: any;
        }) {
          const { seriesName, name, percent } = params;
          return `${seriesName} <br/>${name}: ${percent.toFixed(1)}%`;
        },
      },
      legend: {
        show: !compact,
        orient: 'vertical',
        left: '10px',
        top: 'middle',
        data: pieData.map((item: { name: any }) => item.name),
      },
      series: [
        {
          name: 'Emission Rates',
          type: 'pie',
          radius: '50%',
          data: pieData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
      toolbox: {
        show: !compact,
        right: '1%',
        feature: {
          saveAsImage: {},
          myDownload: {
            show: true,
            title: 'Download data',
            icon: downloadIcon,
            onclick: function () {
              downloadCSV(pieData);
            },
          },
          dataZoom: [
            {
              type: 'slider',
            },
            {
              type: 'inside',
            },
          ],
        },
      },
      animation: false,
    };

    const optionBar = {
      title: {
        text: 'Total Emissions (kg/h)',
        left: 'center',
        ...(compact
          ? {
              textStyle: {
                fontSize: COMPACT_FONT_SIZE.toString() + 'px',
              },
            }
          : {}),
      },
      grid: {
        top: compact ? 40 : 60,
        bottom: compact ? 20 : 40,
        left: compact ? 65 : 50,
        right: compact ? 20 : 30,
      },
      xAxis: {
        type: 'category',
        data: ['AMI', 'Non O&G'],
        name: 'Equipment Type',
        nameLocation: 'middle',
        nameGap: 25,
      },
      yAxis: {
        type: 'value',
        name: 'Total Emissions (kg/h)',
        nameRotate: 90,
        position: 'left',
        offset: 0,
        nameLocation: 'middle',
        nameGap: 50,
      },
      series: [...stackedData, ...normalData],
      tooltip: {
        trigger: 'item',
        formatter: function (params: {
          seriesName: any;
          name: any;
          value: any;
          percent: any;
        }) {
          const { seriesName, name, value } = Array.isArray(params)
            ? params[0]
            : params;
          return `${seriesName} <br/>${name}: ${Number(value || '0').toFixed(1)}`;
        },
      },
      toolbox: {
        show: isFullscreen,
        right: '2%',
        feature: {
          saveAsImage: {},
          myDownload: {
            show: true,
            title: 'Download data',
            icon: downloadIcon,
            onclick: function () {
              downloadCSV([...stackedData, ...normalData]);
            },
          },
          dataZoom: [
            {
              type: 'slider',
            },
            {
              type: 'inside',
            },
          ],
        },
      },
      animation: false,
    };

    chart.setOption(chartType === 'pie' ? optionPie : optionBar);

    return () => {
      chart.dispose();
    };
  }, [results, isFullscreen, chartType, compact]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleChartType = () => {
    setChartType(chartType === 'pie' ? 'bar' : 'pie');
  };

  return (
    <div className={`chart-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <IconButton
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '0px',
          left: '10px',
          zIndex: 1000,
        }}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
      <Button
        onClick={toggleChartType}
        style={{
          position: 'absolute',
          top: '0px',
          right: compact ? '0' : '160px',
          zIndex: 1000,
        }}
      >
        {chartType === 'pie' ? 'Switch to Bar Chart' : 'Switch to Pie Chart'}
      </Button>
      <div ref={chartRef} className={'chart pie'} />
    </div>
  );
};

export default ChartComponent;
